import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Pencil, X } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { img } from '../../mock/mock';

const IMAGE_KEYS = Object.keys(img);
const empty = { title: '', description: '', category: 'events', date: '', location: 'Roderick Gay Botanical Garden', image_key: 'meadow' };

const toLocalInput = (iso) => {
  try { const d = new Date(iso); const off = d.getTimezoneOffset(); const l = new Date(d.getTime() - off * 60000); return l.toISOString().slice(0, 16); } catch { return ''; }
};

const EventsManager = () => {
  const { toast } = useToast();
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get('/events', { params: { all: true } }).then((r) => setEvents(r.data || [])).catch(() => setEvents([]));
  useEffect(() => { load(); }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (ev) => { setEditing(ev.id); setForm({ ...ev, date: toLocalInput(ev.date) }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, date: new Date(form.date).toISOString() };
    try {
      if (editing) await api.put(`/events/${editing}`, payload);
      else await api.post('/events', payload);
      toast({ title: editing ? 'Event updated' : 'Event created' });
      setOpen(false); load();
    } catch { toast({ title: 'Save failed', variant: 'destructive' }); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try { await api.delete(`/events/${id}`); setEvents((x) => x.filter((e) => e.id !== id)); toast({ title: 'Deleted' }); }
    catch { toast({ title: 'Delete failed', variant: 'destructive' }); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-display text-xl text-forest">Events ({events.length})</h3>
        <button onClick={openNew} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gold text-white font-600 text-sm hover:bg-gold-light"><Plus className="w-4 h-4" /> New Event</button>
      </div>

      <div className="space-y-3">
        {events.map((ev) => (
          <div key={ev.id} className="flex items-center gap-4 bg-white rounded-xl border border-sand p-4">
            <div className="flex-1">
              <p className="font-600 text-forest">{ev.title}</p>
              <p className="text-xs text-ink/55">{new Date(ev.date).toLocaleString()} · {ev.location} · <span className="capitalize">{ev.category}</span></p>
            </div>
            <button onClick={() => openEdit(ev)} className="w-9 h-9 rounded-lg bg-sand text-forest hover:bg-forest hover:text-cream flex items-center justify-center"><Pencil className="w-4 h-4" /></button>
            <button onClick={() => remove(ev.id)} className="w-9 h-9 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {events.length === 0 && <p className="text-ink/50 text-sm">No events yet.</p>}
      </div>

      {open && (
        <div className="fixed inset-0 z-[70] bg-forest-dark/60 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-cream rounded-2xl p-7 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-xl text-forest">{editing ? 'Edit Event' : 'New Event'}</h3>
              <button type="button" onClick={() => setOpen(false)}><X className="w-5 h-5 text-ink/50" /></button>
            </div>
            <div className="space-y-4">
              <input required value={form.title} onChange={upd('title')} placeholder="Title" className="w-full px-3 py-2.5 rounded-lg border border-sand text-sm focus:border-gold focus:outline-none" />
              <textarea required rows={3} value={form.description} onChange={upd('description')} placeholder="Description" className="w-full px-3 py-2.5 rounded-lg border border-sand text-sm focus:border-gold focus:outline-none resize-none" />
              <div className="grid grid-cols-2 gap-4">
                <select value={form.category} onChange={upd('category')} className="px-3 py-2.5 rounded-lg border border-sand text-sm focus:border-gold focus:outline-none"><option value="events">Events</option><option value="education">Education</option></select>
                <input required type="datetime-local" value={form.date} onChange={upd('date')} className="px-3 py-2.5 rounded-lg border border-sand text-sm focus:border-gold focus:outline-none" />
              </div>
              <input value={form.location} onChange={upd('location')} placeholder="Location" className="w-full px-3 py-2.5 rounded-lg border border-sand text-sm focus:border-gold focus:outline-none" />
              <div>
                <label className="block text-xs font-600 text-ink/60 mb-1">Image</label>
                <select value={form.image_key} onChange={upd('image_key')} className="w-full px-3 py-2.5 rounded-lg border border-sand text-sm focus:border-gold focus:outline-none">
                  {IMAGE_KEYS.map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
              </div>
            </div>
            <button type="submit" className="w-full mt-6 py-3 rounded-full bg-gold text-white font-600 hover:bg-gold-light">{editing ? 'Save Changes' : 'Create Event'}</button>
          </form>
        </div>
      )}
    </div>
  );
};

export default EventsManager;
